import { CircleAlert, Info, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../cn'

export type CalloutTone = 'info' | 'note' | 'warning' | 'danger'

const TONES: Record<CalloutTone, { box: string; icon: ReactNode }> = {
  note: { box: 'border-leaf bg-leaf-soft/70 text-[#46593a]', icon: <Info /> },
  info: { box: 'border-teal bg-teal-50 text-teal-800', icon: <Info /> },
  warning: { box: 'border-watch bg-watch-soft text-watch', icon: <TriangleAlert /> },
  danger: { box: 'border-danger bg-danger-soft text-danger', icon: <CircleAlert /> },
}

export interface CalloutProps {
  tone?: CalloutTone
  title?: ReactNode
  children: ReactNode
  className?: string
  /** `alert` announces it (form errors); default is a static note. */
  role?: 'alert' | 'status' | 'note'
}

/** Left-ruled explanatory note (prototype `.callout`), also used for inline form errors. */
export function Callout({ tone = 'note', title, children, className, role }: CalloutProps) {
  const style = TONES[tone]
  return (
    <div
      role={role}
      className={cn(
        'flex gap-3 rounded-md border-l-[3px] px-4 py-3 text-sm leading-relaxed',
        style.box,
        className,
      )}
    >
      <span aria-hidden="true" className="mt-0.5 shrink-0 [&>svg]:size-4">
        {style.icon}
      </span>
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className={title ? 'mt-0.5' : undefined}>{children}</div>
      </div>
    </div>
  )
}
